// ChatBoxBottom.jsx (world‑class composer + voice memo bar)
// Backward compatible: all new props optional.
import { useContext, useMemo, useState, useEffect, useRef } from 'react';
import Picker from 'emoji-picker-react';
import useMobileView from '../../hooks/useMobileView';
import { ViewPortContext } from '../../context/ViewPortContext';
import '../createComment/createComment.css';
import './chatBoxBottom.css';
import { ClipLoader } from 'react-spinners';

export default function ChatBoxBottom({
  voiceMemo,
  picker,
  menu,
  handleEmoji,
  textRef,
  register,
  text,
  setText,
  handleSubmit,
  loading,
  sendingFile,
  handleMicClick,
  isRecording,
  setPicker,
  fileInput,
  setSendingFile,
  handleSendAudioMemo,
  parent = 'Messenger',
  variant,

  // Optional enhancements
  setVoiceMemo,
  clearBlobUrl,
  recStatus,
  maxVoiceSeconds = 300,
}) {
  const mobileView = useMobileView();
  const { width } = useContext(ViewPortContext);

  const composerClass = useMemo(() => {
    const classes = ['ccComposer'];
    classes.push(
      variant ? variant : mobileView ? 'ccCompact' : 'ccComfortable',
    );
    return classes.join(' ');
  }, [mobileView, variant]);

  const filled = !!text?.trim().length;
  const sendDisabled = !voiceMemo && !filled;

  // ===== Voice Memo Internal State =====
  const [vmSeconds, setVmSeconds] = useState(0);
  const tickRef = useRef(null);

  useEffect(() => {
    if (voiceMemo && isRecording) {
      tickRef.current = setInterval(() => {
        setVmSeconds((s) => {
          if (s + 1 >= maxVoiceSeconds) {
            try {
              handleMicClick();
            } catch {}
            return maxVoiceSeconds;
          }
          return s + 1;
        });
      }, 1000);
    }
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [voiceMemo, isRecording, maxVoiceSeconds, handleMicClick]);

  useEffect(() => {
    if (!voiceMemo) {
      if (tickRef.current) clearInterval(tickRef.current);
      setVmSeconds(0);
    }
  }, [voiceMemo]);

  const formatTime = (secs) => {
    const m = String(Math.floor(secs / 60)).padStart(2, '0');
    const s = String(secs % 60).padStart(2, '0');
    return `${m}:${s}`;
  };

  const isRecordingActive = voiceMemo && isRecording;
  const hasRecorded = voiceMemo && !isRecording;

  // ===== Voice Memo Actions =====
  const beginVoiceMemo = () => {
    if (!voiceMemo) setVoiceMemo?.(true);
    handleMicClick?.();
  };

  const stopRecording = () => {
    if (isRecordingActive) handleMicClick?.();
  };

  const cancelVoiceMemo = () => {
    try {
      if (isRecordingActive) handleMicClick();
    } catch {}
    setVoiceMemo?.(false);
    clearBlobUrl?.();
    setVmSeconds(0);
  };

  const reRecord = () => {
    clearBlobUrl?.();
    setVmSeconds(0);
    if (!isRecordingActive) handleMicClick?.();
    setVoiceMemo?.(true);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    handleSubmit(e);
  };

  const onEnterKey = (e) => {
    if ((e.key === 'Enter' || e.key === 'Tab') && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <div className="chatBoxBottom">
      <div className={composerClass}>
        <div className="create_comment ccFlexRoot" style={{ width: '100%' }}>
          <div
            className="commentShell ccShell"
            data-filled={filled ? 'true' : 'false'}
          >
            {/* VOICE MEMO BAR */}
            {voiceMemo && (
              <div
                className={
                  'ccVoiceMemoBar ' +
                  (isRecordingActive ? 'vmRecording' : 'vmReview')
                }
              >
                <div className="ccVMRow">
                  <div className="ccVMStatusBlock">
                    {isRecordingActive ? (
                      <span className="ccVMRedDot" aria-label="Recording" />
                    ) : (
                      <span className="ccVMCheck" aria-label="Recorded">
                        ✔
                      </span>
                    )}
                    <span className="ccVMTimer" aria-live="polite">
                      {formatTime(vmSeconds)}
                      {isRecordingActive &&
                        recStatus &&
                        recStatus !== 'recording' &&
                        ` • ${recStatus}`}
                    </span>
                    {isRecordingActive && (
                      <span className="ccVMHint">Recording… Tap Stop</span>
                    )}
                    {!isRecordingActive && hasRecorded && (
                      <span className="ccVMHint">Ready to send</span>
                    )}
                  </div>

                  <div className="ccVMButtons">
                    {isRecordingActive ? (
                      <button
                        type="button"
                        className="ccVMBtn vmStop"
                        onClick={stopRecording}
                      >
                        Stop
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="ccVMBtn vmReRecord"
                        onClick={reRecord}
                      >
                        Re‑record
                      </button>
                    )}
                    <button
                      type="button"
                      className="ccVMBtn vmCancel"
                      onClick={cancelVoiceMemo}
                    >
                      Cancel
                    </button>
                    {hasRecorded && (
                      <button
                        type="button"
                        className="ccVMBtn vmSend"
                        onClick={handleSendAudioMemo}
                      >
                        Send
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* NORMAL MODE */}
            {!voiceMemo && (
              <>
                {picker && (
                  <div className="comment_emoji_picker" ref={menu}>
                    <Picker onEmojiClick={handleEmoji} />
                  </div>
                )}

                <form onSubmit={onSubmit} className="ccInputForm">
                  <input
                    type="text"
                    ref={!mobileView ? textRef : register}
                    value={text}
                    placeholder={
                      parent === 'GalaxyChatBox'
                        ? 'Message the Galaxy...'
                        : 'Type a message...'
                    }
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={onEnterKey}
                    className="commentInputText ccInputGrow"
                    maxLength={11000}
                    aria-label="Chat message input"
                  />
                </form>

                <div className="ccToolCluster">
                  {loading && (
                    <div className="statusCircle" aria-label="Uploading">
                      <ClipLoader size={12} color="#1876f2" loading />
                    </div>
                  )}

                  <div
                    className={
                      'comment_circle_icon ccTool micTool' +
                      (sendingFile ? ' noClicks' : '')
                    }
                    onClick={beginVoiceMemo}
                    title="Voice message"
                    aria-label="Record voice message"
                  >
                    <span
                      className={
                        'ccMicIcon ' + (isRecordingActive ? 'ccMicActive' : '')
                      }
                      aria-hidden="true"
                    >
                      🎤
                    </span>
                  </div>

                  <div
                    className="comment_circle_icon ccTool"
                    onClick={() => setPicker((p) => !p)}
                    title="Emoji"
                    aria-label="Toggle emoji picker"
                  >
                    <span className="ccEmojiSymbol">😊</span>
                  </div>

                  <div
                    className="comment_circle_icon ccTool"
                    onClick={() => {
                      if (fileInput?.current) {
                        fileInput.current.click();
                        setSendingFile?.(true);
                      }
                    }}
                    title="Add image"
                    aria-label="Add image"
                  >
                    <i className="addPhoto_icon" />
                  </div>
                </div>
              </>
            )}
          </div>

          {/* SEND (text mode only) */}
          {!voiceMemo && (
            <button
              type="button"
              className={
                'ccSendBtn inlineSend ' +
                (sendDisabled ? 'ccSendDisabled' : 'ccSendReady')
              }
              disabled={sendDisabled}
              onClick={handleSubmit}
              title="Send"
              aria-label="Send message"
            >
              <span className="ccSendIcon">➤</span>
            </button>
          )}
        </div>
      </div>

      {/* Narrow layout send */}
      {!voiceMemo && width < 418 && (
        <button
          className={
            'ccSendBtn ' + (sendDisabled ? 'ccSendDisabled' : 'ccSendReady')
          }
          style={{ marginTop: '8px', alignSelf: 'flex-end' }}
          onClick={handleSubmit}
          disabled={sendDisabled}
          aria-label="Send message"
        >
          <span className="ccSendIcon">➤</span>
        </button>
      )}
    </div>
  );
}
